import { useState } from "react";
import { CheckCircle2, AlertCircle, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { ToolShell, OptionRow, ToolNav } from "@/pages/tools/_shared";

const QUESTIONS = [
  { key: "consent_explicit", section: "Consent", text: "Do you capture explicit, opt-in consent for every category of personal data you process?" },
  { key: "consent_withdraw", section: "Consent", text: "Can individuals withdraw consent as easily as they gave it?" },
  { key: "notice_public", section: "Privacy notices", text: "Do you publish a privacy notice that clearly explains what data you collect and why?" },
  { key: "notice_updated", section: "Privacy notices", text: "Has your privacy notice been reviewed and updated in the last 12 months?" },
  { key: "dsar_process", section: "Data subject requests", text: "Do you have a defined process to respond to a data subject request within 45 days?" },
  { key: "dsar_log", section: "Data subject requests", text: "Do you log data subject requests and their outcomes for audit purposes?" },
  { key: "dpo_named", section: "DPO", text: "Have you formally named a Data Protection Officer (or equivalent responsible person)?" },
  { key: "dpo_reporting", section: "DPO", text: "Does the DPO report to senior management with independence?" },
  { key: "xborder_map", section: "Cross-border transfers", text: "Do you know which of your systems and vendors process personal data outside Oman?" },
  { key: "xborder_controls", section: "Cross-border transfers", text: "Do you have contractual and technical controls in place for those transfers?" },
  { key: "breach_process", section: "Breach process", text: "Do you have a documented incident response process that can meet a 72-hour breach notification?" },
  { key: "breach_drill", section: "Breach process", text: "Have you tested that process in the last 12 months with a drill or a real incident?" },
];

const OPTIONS = [
  { value: "yes", label: "Yes", weight: 1 },
  { value: "partial", label: "Partial", weight: 0.5 },
  { value: "no", label: "No", weight: 0 },
];

function computeScore(answers) {
  const perSection = {};
  let total = 0;
  QUESTIONS.forEach((q) => {
    const opt = OPTIONS.find((o) => o.value === answers[q.key]);
    const w = opt ? opt.weight : 0;
    total += w;
    perSection[q.section] = perSection[q.section] || { got: 0, max: 0 };
    perSection[q.section].got += w;
    perSection[q.section].max += 1;
  });
  const score = Math.round((total / QUESTIONS.length) * 100);
  const sectionScores = {};
  Object.entries(perSection).forEach(([s, v]) => {
    const pct = v.got / v.max;
    sectionScores[s] = pct >= 0.75 ? "green" : pct >= 0.4 ? "amber" : "red";
  });
  const gaps = QUESTIONS.filter((q) => answers[q.key] !== "yes").slice(0, 3).map((q) => q.text);
  return { score, sectionScores, gaps };
}

export default function PDPLReadiness() {
  const [step, setStep] = useState(0); // 0..QUESTIONS.length-1 questions, then gate
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [state, setState] = useState({ stage: "quiz", error: "" });

  const total = QUESTIONS.length + 1;
  const isQuestion = step < QUESTIONS.length;

  if (state.stage === "result") {
    const { score, sectionScores, gaps } = state.result;
    return (
      <ToolShell eyebrow="PDPL Readiness Check" title="Your readiness score">
        <div className="rounded-2xl border border-wafa-deep/10 bg-wafa-cream/40 p-8 text-center">
          <div className="font-heading text-6xl font-semibold text-wafa-deep">{score}</div>
          <div className="mt-1 text-xs uppercase tracking-widest text-wafa-ink/60">out of 100</div>
        </div>

        <div className="mt-8">
          <div className="font-heading text-lg font-semibold text-wafa-deep">By section</div>
          <div className="mt-4 grid gap-3">
            {Object.entries(sectionScores).map(([s, level]) => {
              const dot = level === "green" ? "bg-wafa-primary" : level === "amber" ? "bg-wafa-sunshine" : "bg-wafa-tomato";
              return (
                <div key={s} data-testid={`pdpl-section-${s}`} className="flex items-center justify-between rounded-md border border-wafa-deep/10 bg-white px-4 py-3 text-sm">
                  <span className="font-medium text-wafa-deep">{s}</span>
                  <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-wafa-ink/70">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`} />
                    {level}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length ? (
          <div className="mt-8">
            <div className="font-heading text-lg font-semibold text-wafa-deep">Your top 3 gaps</div>
            <ul className="mt-3 space-y-2">
              {gaps.map((g, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-wafa-ink/80">
                  <AlertCircle className="mt-0.5 h-4 w-4 text-wafa-carrot" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-10 rounded-lg border border-wafa-deep/10 bg-white p-6">
          <div className="font-heading font-semibold text-wafa-deep">Want us to walk you through it?</div>
          <p className="mt-1 text-sm text-wafa-ink/70">Book a thirty-minute call and we'll turn this into a plan.</p>
          <a href="/contact" className="mt-4 inline-flex rounded-md bg-wafa-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-wafa-deep" data-testid="pdpl-result-cta">
            Book a Free Scoping Call
          </a>
        </div>
      </ToolShell>
    );
  }

  if (state.stage === "gate") {
    const submit = async (e) => {
      e.preventDefault();
      if (!contact.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        setState({ ...state, error: "Please enter a valid email address." });
        return;
      }
      const result = computeScore(answers);
      try {
        await api.post("/tools/pdpl-readiness", {
          answers: Object.entries(answers).map(([key, value]) => ({ key, value })),
          score: result.score,
          section_scores: result.sectionScores,
          gaps: result.gaps,
          email: contact.email,
          name: contact.name,
          company: contact.company,
        });
        setState({ stage: "result", result });
      } catch (err) {
        const msg = err?.response?.data?.detail || "Something went wrong.";
        setState({ ...state, error: typeof msg === "string" ? msg : "Failed to send." });
      }
    };
    return (
      <ToolShell eyebrow="PDPL Readiness Check" title="See your score" subtitle="We'll email you a copy for your records." step={total} totalSteps={total}>
        <form onSubmit={submit} className="space-y-4" data-testid="pdpl-gate-form">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep" htmlFor="pdpl-name">Your name</label>
            <input id="pdpl-name" data-testid="pdpl-name" className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep" htmlFor="pdpl-email">Work email *</label>
            <input id="pdpl-email" type="email" required data-testid="pdpl-email" className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep" htmlFor="pdpl-company">Company</label>
            <input id="pdpl-company" data-testid="pdpl-company" className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
          </div>
          {state.error ? (
            <div className="flex items-center gap-2 text-sm text-wafa-tomato" data-testid="pdpl-error">
              <AlertCircle className="h-4 w-4" /> {state.error}
            </div>
          ) : null}
          <button type="submit" className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep" data-testid="pdpl-see-score">
            See my score
          </button>
        </form>
      </ToolShell>
    );
  }

  const q = QUESTIONS[step];
  const answered = !!answers[q.key];
  return (
    <ToolShell
      eyebrow="PDPL Readiness Check"
      title="12 questions. 3 minutes."
      subtitle="Yes / Partial / No — answer for how things actually work today, not the plan."
      step={step + 1}
      totalSteps={total}
    >
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-wafa-primary">{q.section}</div>
        <div className="mt-2 font-heading text-xl font-medium text-wafa-deep sm:text-2xl">{q.text}</div>
        <div className="mt-6 space-y-2.5">
          {OPTIONS.map((o) => (
            <OptionRow
              key={o.value}
              value={o.value}
              current={answers[q.key]}
              onChange={(v) => setAnswers({ ...answers, [q.key]: v })}
              label={o.label}
              testId={`pdpl-option-${q.key}-${o.value}`}
            />
          ))}
        </div>
      </div>
      <ToolNav
        onBack={step > 0 ? () => setStep(step - 1) : null}
        onNext={() => {
          if (step === QUESTIONS.length - 1) setState({ stage: "gate", error: "" });
          else setStep(step + 1);
        }}
        disableNext={!answered}
        nextLabel={step === QUESTIONS.length - 1 ? "Finish" : "Next"}
      />
    </ToolShell>
  );
}
